// Ô chọn vật tư bao bì dùng chung: tìm theo API (debounce), cuộn tải thêm,
// option nhiều dòng.
import { useMemo, useRef, useState } from 'react'

import type { PackagingMaterial } from '@/lib/api/packaging-api'
import { LotOptionRow } from '@/components/lots/lot-option-row'
import { Combobox } from '@/components/ui/combobox'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useInfinitePackagingMaterials,
    usePackagingMaterial,
} from '@/hooks/use-packaging-query'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export interface PickedPackagingMaterial {
    id: string
    name: string
    sku: string
    unit: string
    /** Đơn giá bán vật tư (giá vật tư); >0 → tính tiền vào đơn. */
    sale_price: number
    /** Theo lô hay theo số lượng — form phiếu nhập ẩn/hiện ô lô theo đây. */
    tracking_mode: PackagingMaterial['tracking_mode']
    /** Tồn khả dụng gộp các lô lúc chọn (M-72) — sổ kiểm kê túi hiện và lưu số này. */
    stock_available?: number
}

interface Props {
    /** id đã có trong list — sẽ bị filter ra để tránh trùng. */
    excludeIds?: string[]
    onPick: (material: PickedPackagingMaterial) => void
    placeholder?: string
    disabled?: boolean
    /** Giữ lại lựa chọn sau khi chọn (dùng cho ô chọn một, VD BOM / phiếu nhập).
     *  Mặc định false: reset về rỗng để thêm tiếp nhiều dòng. */
    value?: string
    /** Chỉ lấy vật tư đang sử dụng. Mặc định true. */
    activeOnly?: boolean
    /** Chỉ vật tư đã bật "Kiểm bằng sổ kiểm túi" (#M-69) — ô chọn túi của sổ
     *  kiểm kê túi. Lọc ở server. */
    isCountLogged?: boolean
    /** Nhãn của vật tư đang chọn khi caller đã biết sẵn (vd tên túi trên dòng
     *  sổ đang sửa). Có nó thì không phải gọi API chi tiết vật tư — vai trò ghi
     *  sổ xưởng không có quyền đó. */
    selectedLabel?: string
}

export function PackagingMaterialPicker({
    excludeIds = [],
    onPick,
    placeholder,
    disabled,
    value,
    activeOnly = true,
    isCountLogged = false,
    selectedLabel,
}: Props) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)

    const query = useInfinitePackagingMaterials({
        ...(activeOnly ? { is_active: true } : {}),
        ...(isCountLogged ? { is_count_logged: true } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
    })
    // Destructure ra primitive + hàm ổn định: truyền cả object query xuống
    // Combobox sẽ tạo callback mới mỗi render và làm observer ngắt/nối liên tục.
    const { hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } = query

    const materials: PackagingMaterial[] = useMemo(
        () => query.data?.pages.flatMap((p) => p.items) ?? [],
        [query.data],
    )

    // Nhớ mọi vật tư từng tải, để vật tư đã chọn giữ được nhãn khi đổi từ khoá
    // làm nó rơi khỏi trang hiện tại. Ghi trong render: idempotent, cùng pattern
    // `seenRef` của ProductPicker.
    const seenRef = useRef<Map<string, PackagingMaterial>>(new Map())
    for (const mt of materials) seenRef.current.set(mt.id, mt)
    const seen = value ? seenRef.current.get(value) : undefined

    // Giá trị đã chọn sẵn có thể nằm ở trang chưa tải (VD mở lại dòng BOM cũ),
    // khi đó ô sẽ hiện placeholder thay vì tên vật tư. Chỉ gọi khi thật sự
    // thiếu: chưa từng thấy và caller không đưa sẵn nhãn.
    const foundSelected = materials.some((mt) => mt.id === value)
    const fallback = usePackagingMaterial(
        value,
        Boolean(value) && !foundSelected && !seen && !selectedLabel,
    )

    // Ghép bản ghi dự phòng vào đầu để trigger hiển thị đúng tên. Phải dùng
    // chung danh sách này cho cả options lẫn handleChange, nếu không chọn lại
    // chính bản ghi dự phòng sẽ im lặng không làm gì.
    //
    // Nhưng KHÔNG ghép khi đang tìm kiếm: nó không khớp từ khoá, hiện lên giữa
    // kết quả tìm sẽ trông như một kết quả hợp lệ. Lúc đó danh sách phải đúng
    // là những gì server trả về.
    const allMaterials = useMemo(
        () =>
            fallback.data && !foundSelected && !debouncedSearch
                ? [fallback.data, ...materials]
                : materials,
        [fallback.data, foundSelected, materials, debouncedSearch],
    )

    const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds])
    const options = useMemo(
        () =>
            allMaterials
                .filter((mt) => !excludeSet.has(mt.id))
                .map((mt) => ({
                    value: mt.id,
                    // Nhãn vẫn là chuỗi phẳng: Combobox dùng nó cho phần hiển
                    // thị khi đã chọn. Phần dropdown mới dùng `render`.
                    label: `${mt.name} (${mt.sku})`,
                    render: () => (
                        <LotOptionRow
                            title={mt.name}
                            code={mt.sku}
                            chips={[mt.unit]}
                            qty={formatQty(mt.stock_available ?? 0)}
                        />
                    ),
                })),
        [allMaterials, excludeSet],
    )

    // Ô tìm kiếm nằm trong popover bị Radix unmount khi đóng; reset state search
    // theo, nếu không mở lại thấy ô rỗng mà danh sách vẫn lọc theo từ khoá cũ.
    const handleOpenChange = (open: boolean) => {
        if (!open) setSearch('')
    }

    // Nhãn trên nút cho vật tư không có trong danh sách hiện tại.
    const selectedMaterial = seen ?? fallback.data ?? undefined
    const selectedOption = value
        ? selectedMaterial
            ? {
                  value,
                  label: `${selectedMaterial.name} (${selectedMaterial.sku})`,
              }
            : selectedLabel
              ? { value, label: selectedLabel }
              : undefined
        : undefined

    const handleChange = (id: string) => {
        const mat = allMaterials.find((mt) => mt.id === id)
        if (!mat) return
        onPick({
            id: mat.id,
            name: mat.name,
            sku: mat.sku,
            unit: mat.unit,
            sale_price: mat.sale_price ?? 0,
            tracking_mode: mat.tracking_mode,
            stock_available: mat.stock_available,
        })
    }

    return (
        <Combobox
            options={options}
            value={value ?? ''}
            onChange={handleChange}
            selectedOption={selectedOption}
            onSearchChange={setSearch}
            onOpenChange={handleOpenChange}
            shouldFilter={false}
            placeholder={placeholder ?? m.packaging_picker_placeholder()}
            searchPlaceholder={m.packaging_picker_search_placeholder()}
            emptyText={m.packaging_picker_empty()}
            loading={isLoading}
            disabled={disabled}
            className="w-full"
            onLoadMore={fetchNextPage}
            hasMore={hasNextPage}
            loadingMore={isFetchingNextPage}
        />
    )
}
